import React, { Component } from 'react';
import './ftue.css';
import FtueToast from './FtueToast';


// Spotlight system tunables. The viewport gate keeps the dim overlay +
// pointer line + highlight box off small screens (the toast still
// fires there via the back-compat path). Padding pads the cutout so the
// target isn't flush against the bright edge.
const DESKTOP_MIN_WIDTH = 900;
const SPOTLIGHT_PADDING = 8;
const FTUE_TARGET_ATTRIBUTE = 'data-ftue-target';
// Master kill switch for the entire FTUE visible layer — dim overlay,
// connecting line, highlight box, AND the toast itself. When false,
// FtueController renders nothing and the SceneRunner's advance chrome
// is also suppressed (via the same flag, imported there). Step
// dispatch + FTUE_ACK still run on the wire so scenes get recorded as
// seen; the UI is just silent. Flip back to true to re-enable,
// pending design approval.
export const OVERLAYS_ENABLED = false;
// Our own overlay/box/line live inside this wrapper. The MutationObserver
// ignores mutations originating here so React's style writes (and the
// imperative line redraw) don't feed back into another recompute.
const FTUE_LAYER_SELECTOR = '.ftue-main-element';


function GetScreenCordinates(obj) {
    var p = {};
    p.x = obj.offsetLeft;
    p.y = obj.offsetTop;
    while (obj.offsetParent) {
        p.x = p.x + obj.offsetParent.offsetLeft;
        p.y = p.y + obj.offsetParent.offsetTop;
        if (obj == document.getElementsByTagName("body")[0]) {
            break;
        }
        else {
            obj = obj.offsetParent;
        }
    }
    return p;
}




function getOffset(el) {
    let position = el.getBoundingClientRect();
    return {
        left: position.left + window.scrollX,
        top: position.top + window.scrollY
    };
};


function adjustLine(from, to, line) {
    // https://stackoverflow.com/questions/19382872/how-to-connect-html-divs-with-lines

    let fromCoordinates = GetScreenCordinates(from);
    let toCoordinates = GetScreenCordinates(to);


    let fromPosition = getOffset(from);
    let toPosition = getOffset(to);
    let fromTop = fromPosition.top// + from.offsetHeight / 2;
    let toTop = toPosition.top// + to.offsetHeight / 2;
    let fromLeft = fromPosition.left// + from.offsetWidth / 2;
    let toLeft = toPosition.left// + to.offsetWidth / 2;

    // let fromTop = from.offsetTop + from.offsetHeight / 2;
    // let toTop = to.offsetTop + to.offsetHeight / 2;
    // let fromLeft = from.offsetLeft + from.offsetWidth / 2;
    // let toLeft = to.offsetLeft + to.offsetWidth / 2;

    let CA = Math.abs(toTop - fromTop);
    let CO = Math.abs(toLeft - fromLeft);
    let H = Math.sqrt(CA * CA + CO * CO);
    let ANG = 180 / Math.PI * Math.acos(CA / H);

    let topPosition;
    if (toTop > fromTop) {
        topPosition = (toTop - fromTop) / 2 + fromTop;
    } else {
        topPosition = (fromTop - toTop) / 2 + toTop;
    }
    let leftPosition;
    if (toLeft > fromLeft) {
        leftPosition = (toLeft - fromLeft) / 2 + fromLeft;
    } else {
        leftPosition = (fromLeft - toLeft) / 2 + toLeft;
    }

    if ((fromTop < toTop && fromLeft < toLeft) || (toTop < fromTop && toLeft < fromLeft) || (fromTop > toTop && fromLeft > toLeft) || (toTop > fromTop && toLeft > fromLeft)) {
        ANG *= -1;
    }
    topPosition -= H / 2;

    line.style['-webkit-transform'] = 'rotate(' + ANG + 'deg)';
    line.style['-moz-transform'] = 'rotate(' + ANG + 'deg)';
    line.style['-ms-transform'] = 'rotate(' + ANG + 'deg)';
    line.style['-o-transform'] = 'rotate(' + ANG + 'deg)';
    line.style['-transform'] = 'rotate(' + ANG + 'deg)';
    line.style.top = topPosition + 'px';
    line.style.left = leftPosition + 'px';
    line.style.height = H + 'px';
}


function drawBoundingBox(el) {
    let rect = el.getBoundingClientRect();
    const div = document.createElement("div");
    // Might be needed on crazy pages, but makes the console output for the div crazy large.
    // div.style.all = "unset";
    div.style.position = "absolute";
    div.style.zIndex = "2147483647";
    // At least _try_ to scroll along. Won’t work for inner scroll.
    div.style.left = `${window.scrollX + rect.left}px`;
    div.style.top = `${window.scrollY + rect.top}px`;
    div.style.width = `${rect.width}px`;
    div.style.height = `${rect.height}px`;
    div.style.boxSizing = "border-box";
    div.style.border = "1px solid magenta";
    div.style.pointerEvents = "none";
    document.documentElement.append(div);
    return div;
}


// Punch a rectangular cutout out of a full-viewport rectangle. The
// outer ring is the viewport (0 0 → 100% 0 → 100% 100% → 0 100%); the
// inner ring is the target rect; the two are joined by a zero-width
// corridor on the left edge. Everything outside the cutout is dimmed.
function buildSpotlightClipPath(top, right, bottom, left) {
    return 'polygon('
        + '0 0, 100% 0, 100% 100%, 0 100%, '
        + `0 ${top}px, ${left}px ${top}px, `
        + `${left}px ${bottom}px, ${right}px ${bottom}px, `
        + `${right}px ${top}px, 0 ${top}px)`;
}




export default class FtueController extends Component {

    constructor(props) {
        super(props);
        this.state = {
            lineFrom: null,
            lineTo: null,
            rerender: false,
            showToast: false,
            message: '',
            targetName: null
        }
        this.lineElement = null;
        this.highlightElement = null;

        // Spotlight target tracking. Observers stay null until a desktop
        // FTUE step with a resolvable target activates them; they are
        // torn down on message clear, target change, and unmount.
        this.resizeObserver = null;
        this.mutationObserver = null;
        this.rafHandle = null;
        this.observedTarget = null;

        // The scripted-FTUE scene runner registers here (see
        // FtueSceneRunner). The live FTUE_STEP path forwards through
        // app.ftueSceneRunner; this handle is the controller-mediated
        // reach path so the message layer can also drive steps via the
        // controller it already holds.
        this.sceneRunner = null;

        this.enabled = false;
    }

    setSceneRunner = (runner) => {
        this.sceneRunner = runner;
    }

    playSceneStep = (step) => {
        if (this.sceneRunner) {
            this.sceneRunner.playStep(step);
        }
    }


    componentDidMount() {
        // console.log('FtueController.mount', 'props', this.props);
        this.props.app.ftueController = this;
        window.addEventListener('resize', this.rerender);

        // setTimeout(this.exampleFtueMessage, 2000);
    }

    componentWillUnmount() {
        // StrictMode-safe: only detach listeners/observers here. No
        // navigation, no group-leave, no persistent side-effects.
        window.removeEventListener('resize', this.rerender);
        this.teardownTargetTracking();
        // this.clearBox();
    }


    componentDidUpdate() {
        // Redraw the connecting line imperatively after every render the
        // spotlight is active for — the clip-path + box are declarative,
        // the line geometry is not. Cheap no-op when no target.
        this.updateLine();
    }

    rerender = () => {
        // console.log('ftue rerender');
        this.setState({rerender:!this.state.rerender});
    }


    lineTo = (fromId, toId) => {
        let fromElement = document.getElementById(fromId);
        let toElement = document.getElementById(toId);
        // adjustLine(fromElement, toElement, this.lineElement);
        // let toBox = drawBoundingBox(toElement);
        this.setState({
            fromElement: fromElement,
            toElement: toElement
        });
    };

    clearBox = () => {
        console.log('Clear Box')
        if (this.state.toBox) {
            this.state.toBox.parentNode.removeChild(this.state.toBox);
            this.setState({toBox: null});
        }
    }

    updateDimensions = () => {
        if (!this.state.toElement) return;
        adjustLine(
            this.state.fromElement, this.state.toElement, this.lineElement);
        this.clearBox();
        let toBox = drawBoundingBox(this.state.toElement);
        this.setState({
            toBox: toBox
        });
    }

    boxStyle = () => {
        if (!this.state.toElement) return {visibility: 'hidden'};
        let rect = this.state.toElement.getBoundingClientRect();
        let left = `${window.scrollX + rect.left}px`
        let top = `${window.scrollY + rect.top}px`;
        let width = `${rect.width}px`;
        let height = `${rect.height}px`;
        return {
            left:left,
            top:top,
            width:width,
            height:height
        }
    }

    exampleFtueMessage = () => {
        let message = {
            message: 'Example FTUE Message',
            target: 'shop-button'
        }
        let toElement = document.getElementById(message.target);
        let fromElement = document.getElementById('toaster');
        this.setState({toElement:toElement, fromElement: fromElement},
            this.updateConnectingLine);
    }

    updateConnectingLine = () => {
        adjustLine(
            this.state.fromElement, this.state.toElement, this.lineElement);
    }

    // --- Spotlight system (data-attribute-driven) -------------------

    isFtueEnabled = () => {
        const app = this.props.app;
        return !!(app && app.state && app.state.authenticator
            && app.state.authenticator.account
            && app.state.authenticator.account.ftue);
    }

    isDesktopViewport = () => {
        return window.innerWidth >= DESKTOP_MIN_WIDTH;
    }

    findTarget = (targetName) => {
        if (!targetName) return null;
        return document.querySelector(
            `[${FTUE_TARGET_ATTRIBUTE}="${targetName}"]`);
    }

    setMessage = (message, targetName = null) => {
        // Any prior target's observers go first — a new step never leaks
        // the old step's ResizeObserver/MutationObserver.
        this.teardownTargetTracking();
        this.setState(
            { showToast: true, message: message, targetName: targetName },
            this.setupTargetTracking);
    }

    clearMessage = () => {
        // Drop the whole FTUE layer: toast, dim overlay, line, box.
        // Called by FtueSceneRunner when the user acks the final step
        // of a scene — without this the spotlight strands on screen
        // because the runner only resets its own queue/state.
        this.teardownTargetTracking();
        this.setState({
            showToast: false,
            message: '',
            targetName: null,
        });
    }

    setupTargetTracking = () => {
        const targetName = this.state.targetName;
        // Pure back-compat toast (no target), FTUE disabled, or a
        // viewport too small to fight for pixels: no spotlight, no
        // observers. The toast still renders in every case.
        if (!OVERLAYS_ENABLED) return;
        if (!targetName) return;
        if (!this.isFtueEnabled()) return;
        if (!this.isDesktopViewport()) return;
        const target = this.findTarget(targetName);
        if (!target) {
            console.warn(
                '[FtueController] No element with %s="%s"; rendering '
                + 'toast without spotlight.',
                FTUE_TARGET_ATTRIBUTE, targetName);
            return;
        }
        this.attachObservers(target);
        this.scheduleRecompute();
    }

    attachObservers = (target) => {
        this.observedTarget = target;
        // ResizeObserver: the target itself resizing (content, font
        // load, FloatingWindow body resize) moves its rect.
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(this.scheduleRecompute);
            this.resizeObserver.observe(target);
        }
        // MutationObserver: FloatingWindow drag/resize mutates inline
        // `style` on the react-rnd wrapper, which moves the target
        // without resizing it. Watch style/class mutations across the
        // body subtree; ignore our own layer (see handleMutations).
        this.mutationObserver = new MutationObserver(this.handleMutations);
        this.mutationObserver.observe(document.body, {
            attributes: true,
            subtree: true,
            attributeFilter: ['style', 'class']
        });
    }

    teardownTargetTracking = () => {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
            this.mutationObserver = null;
        }
        if (this.rafHandle) {
            cancelAnimationFrame(this.rafHandle);
            this.rafHandle = null;
        }
        this.observedTarget = null;
    }

    handleMutations = (records) => {
        for (let i = 0; i < records.length; i++) {
            const node = records[i].target;
            // Skip mutations on our own overlay/box/line — React's style
            // writes and the imperative line redraw would otherwise feed
            // back into an infinite recompute loop.
            if (node && node.closest
                    && node.closest(FTUE_LAYER_SELECTOR)) {
                continue;
            }
            this.scheduleRecompute();
            return;
        }
    }

    scheduleRecompute = () => {
        // rAF-throttle: many mutations during a single drag frame
        // coalesce into one recompute. Event-driven only — no polling.
        if (this.rafHandle) return;
        if (typeof requestAnimationFrame === 'undefined') {
            this.rerender();
            return;
        }
        this.rafHandle = requestAnimationFrame(() => {
            this.rafHandle = null;
            this.rerender();
        });
    }

    spotlightTarget = () => {
        // The single source of truth for whether the spotlight renders:
        // FTUE on, a target named, desktop viewport, and the target
        // actually in the DOM. Returns the element or null.
        if (!OVERLAYS_ENABLED) return null;
        if (!this.state.targetName) return null;
        if (!this.isDesktopViewport()) return null;
        return this.findTarget(this.state.targetName);
    }

    updateLine = () => {
        if (!this.lineElement) return;
        const from = document.getElementById('toaster');
        const to = this.spotlightTarget();
        if (!from || !to) return;
        adjustLine(from, to, this.lineElement);
    }

    renderSpotlight = (target) => {
        const rect = target.getBoundingClientRect();
        const pad = SPOTLIGHT_PADDING;
        const top = Math.max(0, rect.top - pad);
        const left = Math.max(0, rect.left - pad);
        const right = rect.right + pad;
        const bottom = rect.bottom + pad;
        const clipPath = buildSpotlightClipPath(top, right, bottom, left);
        const boxStyle = {
            top: `${top}px`,
            left: `${left}px`,
            width: `${right - left}px`,
            height: `${bottom - top}px`
        };
        return <>
            <div className={'ftue-dim-overlay'}
                 style={{ clipPath: clipPath }}
            />
            <div id={'ftue-line'} className={'ftue-line'}
                 ref={element => { this.lineElement = element; }}
            />
            <div id={'ftue-highlight-box'}
                 className={'ftue-highlight-box'}
                 style={boxStyle}
                 ref={element => { this.highlightElement = element; }}
            />
        </>;
    }


    render() {
        // Master kill switch — nothing visible from the FTUE layer
        // until OVERLAYS_ENABLED flips back to true. No wrapper, no
        // toast, no spotlight. Dispatch and FTUE_ACK still run.
        if (!OVERLAYS_ENABLED) return <></>;

        let app = this.props.app;
        let showFTUE = (
            app.state.authenticator &&
            app.state.authenticator.account &&
            app.state.authenticator.account.ftue);

        if (!showFTUE) return <></>;
        if (!this.state.showToast) return <>
            {/*<hr style={{color: 'white'}}/>*/}
        </>;

        const target = this.spotlightTarget();

        return <>
            <div id={'FTUE Element'}
                 className={'ftue-main-element'}
                 style={{pointerEvents: 'none'}}
            >
                {target && this.renderSpotlight(target)}
                <FtueToast
                    ftueMessage={this.state.message}
                    closeToast={() => {this.setState({
                        showToast: false
                    })}}
                />
            </div>

        </>;
    }

};
